import { useEffect, useMemo, useRef, useState } from "react";

/** Lo común entre lo que se proyecta y el celular. Los estilos están en `reto.css`. */

const LETRAS = ["A", "B", "C", "D"];

/** La letra de una opción, con la inclinación de las barras del logo. */
export function LetraOpcion({ indice }: { indice: number }) {
  return (
    <span aria-hidden="true" className="reto-letra">
      <span>{LETRAS[indice] ?? ""}</span>
    </span>
  );
}

/** Las tres barras del logo, gigantes y lentas, detrás de todo. */
export function FondoDeReto() {
  return (
    <div aria-hidden="true" className="reto-fondo">
      <span />
      <span />
      <span />
    </div>
  );
}

/** Las tres barras latiendo: "espera, ya viene". */
export function BarrasLatiendo({ alto = 34 }: { alto?: number }) {
  return (
    <svg
      className="reto-latido mx-auto"
      width={Math.round((alto * 62.4) / 32)}
      height={alto}
      viewBox="0 0 62.4 32"
      aria-hidden="true"
    >
      <g fill="var(--color-rojo-marca)" transform="translate(10.4,0) skewX(-18)">
        <rect x="0" y="0" width="8" height="32" rx="1" />
        <rect x="14" y="0" width="8" height="32" rx="1" />
        <rect x="28" y="0" width="8" height="32" rx="1" />
      </g>
    </svg>
  );
}

/** Una barra por pregunta: las hechas en blanco y la actual en rojo. */
export function PasosDelReto({ actual, total }: { actual: number; total: number }) {
  return (
    <div className="reto-pasos" role="img" aria-label={`Pregunta ${actual} de ${total}`}>
      {Array.from({ length: total }, (_, i) => i + 1).map((n) => (
        <span
          key={n}
          className={n < actual ? "reto-paso-hecho" : n === actual ? "reto-paso-actual" : ""}
        />
      ))}
    </div>
  );
}

/** Milisegundos que faltan: arranca del dato del servidor y baja solo entre consultas. */
function useFaltanMs(restanteMs: number | null, clave: string): number {
  const [fin, setFin] = useState(0);
  const [ahora, setAhora] = useState(() => Date.now());
  // biome-ignore lint/correctness/useExhaustiveDependencies: `clave` reinicia el reloj en cada pregunta
  useEffect(() => {
    if (restanteMs !== null) setFin(Date.now() + restanteMs);
  }, [restanteMs, clave]);
  useEffect(() => {
    const reloj = setInterval(() => setAhora(Date.now()), 250);
    return () => clearInterval(reloj);
  }, []);
  return Math.max(fin - ahora, 0);
}

/** La barra que se vacía y los segundos. En los últimos cinco se pone en rojo. */
export function Reloj({
  restanteMs,
  segundos,
  clave,
}: {
  restanteMs: number | null;
  segundos: number;
  clave: string;
}) {
  const faltan = useFaltanMs(restanteMs, clave);
  if (restanteMs === null) return null;
  const quedan = Math.ceil(faltan / 1000);
  return (
    <div className="flex items-center gap-3">
      <div className={`reto-reloj ${quedan <= 5 ? "reto-reloj-urgente" : ""}`}>
        <div style={{ width: `${Math.min((faltan / (segundos * 1000)) * 100, 100)}%` }} />
      </div>
      <span className="w-9 text-right font-display text-[20px] font-extrabold tabular-nums">
        {quedan}
      </span>
    </div>
  );
}

/** Sube de cero al valor, para que los puntos se sientan ganados. */
export function NumeroQueSube({ valor }: { valor: number }) {
  const [visto, setVisto] = useState(0);
  useEffect(() => {
    const inicio = performance.now();
    let cuadro = 0;
    const paso = (t: number) => {
      const parte = Math.min((t - inicio) / 700, 1);
      setVisto(Math.round(valor * (1 - (1 - parte) ** 3)));
      if (parte < 1) cuadro = requestAnimationFrame(paso);
    };
    cuadro = requestAnimationFrame(paso);
    return () => cancelAnimationFrame(cuadro);
  }, [valor]);
  return <>{visto}</>;
}

const COLORES = ["#c8352e", "#ffffff", "#5dd39e", "#e9eef6"];
const INGREDIENTES = ["🌶️", "🧀", "🌽", "🍋"];

/** Una lluvia corta de barritas de la marca, con algún ingrediente en medio. */
export function Confeti({ cantidad = 46 }: { cantidad?: number }) {
  const piezas = useMemo(
    () =>
      Array.from({ length: cantidad }, (_, id) => ({
        id,
        left: `${Math.random() * 100}%`,
        animationDelay: `${Math.random() * 0.7}s`,
        "--reto-dura": `${2 + Math.random() * 1.6}s`,
        "--reto-giro": `${200 + Math.random() * 500}deg`,
        ingrediente: id % 7 === 0 ? INGREDIENTES[id % INGREDIENTES.length] : null,
        background: COLORES[id % COLORES.length],
      })),
    [cantidad],
  );
  return (
    <div aria-hidden="true" className="reto-confeti">
      {piezas.map(({ id, ingrediente, background, ...estilo }) =>
        ingrediente ? (
          <span key={id} style={{ ...estilo, fontSize: 24 } as React.CSSProperties}>
            {ingrediente}
          </span>
        ) : (
          <span
            key={id}
            className="reto-papel"
            style={{ ...estilo, background } as React.CSSProperties}
          />
        ),
      )}
    </div>
  );
}

type Reaccion = { id: number; emoji: string };
type Flotante = Reaccion & { estilo: React.CSSProperties };

/** Los emojis que manda la gente, subiendo por la pantalla. Cada uno aparece una sola vez. */
export function ReaccionesFlotantes({ reacciones }: { reacciones: Reaccion[] }) {
  const vistas = useRef(new Set<number>());
  const [flotantes, setFlotantes] = useState<Flotante[]>([]);
  useEffect(() => {
    const nuevas = reacciones.filter((r) => !vistas.current.has(r.id));
    if (nuevas.length === 0) return;
    for (const r of nuevas) vistas.current.add(r.id);
    setFlotantes((v) => [
      ...v,
      ...nuevas.map((r) => ({
        ...r,
        estilo: {
          left: `${4 + Math.random() * 92}%`,
          "--reto-deriva": `${(Math.random() - 0.5) * 120}px`,
          "--reto-giro": `${(Math.random() - 0.5) * 40}deg`,
        } as React.CSSProperties,
      })),
    ]);
  }, [reacciones]);
  return (
    <div aria-hidden="true" className="reto-reacciones">
      {flotantes.map((f) => (
        <span
          key={f.id}
          className="reto-reaccion"
          style={f.estilo}
          onAnimationEnd={() => setFlotantes((v) => v.filter((x) => x.id !== f.id))}
        >
          {f.emoji}
        </span>
      ))}
    </div>
  );
}
